// ===================================
// KAIXO - Firebase Cloud Messaging (Android nativo)
// ===================================

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';

// Versión del flujo de permisos. Cambiar este string fuerza un re-prompt
// la PRÓXIMA vez que cualquier usuario abra la app. Útil cuando:
//   - se añade un canal nuevo de notificaciones
//   - se cambia el comportamiento (ej. ahora suena en lock screen)
//   - hay testers viejos que no recibieron el prompt original
// Tras 2026-04-30 v2: añadido canal kaixo_messages MAX importance.
// Tras 2026-05-04 v3: añadido canal kaixo_calls separado para llamadas.
// Tras 2026-05-04 v4: kaixo_calls usa sonido custom kaixo_ringtone.mp3.
// IMPORTANTE: cambiar este string fuerza recreación de los canales —
// Android NO permite cambiar `sound` en un canal existente. El primer
// arranque tras OTA tiene que volver a crearlos para que aplique.
const PERMISSION_PROMPT_VERSION = '2026-05-04-v4';

export async function registerFCMNotifications(userId) {
    // Solo funciona en plataforma nativa (Android/iOS)
    if (!Capacitor.isNativePlatform()) return;

    try {
        // Comprobar estado actual ANTES de pedir.
        const current = await PushNotifications.checkPermissions();
        const lastVersion = localStorage.getItem('kaixo_perm_v') || '';
        const versionChanged = lastVersion !== PERMISSION_PROMPT_VERSION;

        // Si ya está concedido y el flow no cambió, nada que hacer.
        // Si cambió la versión o nunca pedimos, forzar prompt una vez.
        if (current.receive === 'granted' && !versionChanged) {
            // Continuar al flujo de registro/canal sin pedir permiso de nuevo
        } else {
            // Pedir permiso (muestra el prompt si está en 'default')
            const permission = await PushNotifications.requestPermissions();
            // Marcar versión para no volver a invocar el prompt en cada app open
            try { localStorage.setItem('kaixo_perm_v', PERMISSION_PROMPT_VERSION); } catch {}

            if (permission.receive !== 'granted') {
                console.warn('⚠️ Permiso de notificaciones denegado o no concedido:', permission.receive);
                // Aviso visible al usuario UNA vez por sesión, no spam.
                if (permission.receive === 'denied') {
                    try {
                        window.dispatchEvent(new CustomEvent('show_notification', {
                            detail: {
                                message: 'Las notificaciones están desactivadas. Actívalas en Ajustes del móvil → KAIXO → Notificaciones para recibir mensajes con la pantalla bloqueada.',
                                duration: 8000
                            }
                        }));
                    } catch {}
                }
                return;
            }
        }

        // Crear canal de notificaciones Android — IMPRESCINDIBLE para que
        // suene + aparezca en lock screen en Android 8+. Sin canal con
        // importance MAX (5), las notificaciones llegan silenciosas.
        // En iOS este método no hace nada, no rompe.
        try {
            await PushNotifications.createChannel({
                id: 'kaixo_messages',
                name: 'Mensajes',
                description: 'Notificaciones de mensajes nuevos en chats y grupos',
                importance: 5,           // MAX — suena + popup en lock screen
                visibility: 1,           // PUBLIC — texto visible en lock screen
                sound: 'default',
                lights: true,
                vibration: true
            });
            // Canal SEPARADO para llamadas. Android NO permite cambiar
            // `sound` en un canal existente — para que el ringtone nuevo
            // aplique a usuarios que ya tenían el canal creado con
            // sound:'default', BORRAMOS y RECREAMOS. En la primera
            // creación deleteChannel falla silenciosamente y no rompe.
            try { await PushNotifications.deleteChannel({ id: 'kaixo_calls' }); } catch {}
            await PushNotifications.createChannel({
                id: 'kaixo_calls',
                name: 'Llamadas',
                description: 'Notificaciones de llamadas entrantes (voz y vídeo)',
                importance: 5,
                visibility: 1,
                // mp3 bundleado en res/raw/kaixo_ringtone.mp3 (sin extensión)
                // Tono "ring-ring" PSTN clásico: 440Hz + 480Hz, 2s ring / 4s silencio.
                // Volumen lo respeta Android del stream system (botones del móvil).
                sound: 'kaixo_ringtone',
                lights: true,
                vibration: true
            });
        } catch (e) {
            // En iOS PushNotifications.createChannel no existe — ignorar
            console.log('createChannel no aplicable (iOS u otro):', e?.message);
        }

        // CRÍTICO: limpiar listeners anteriores antes de registrar de
        // nuevo. registerFCMNotifications() se llama por cada cambio de
        // currentUser._id (login, switch de cuenta, logout+login). Sin
        // esta limpieza los listeners se acumulan y un solo tap en una
        // notificación dispara el handler N veces (una por sesión). Antes
        // del fix esto causaba navegación múltiple y N envíos del token.
        try { await PushNotifications.removeAllListeners(); } catch {}

        // Registrar para push
        await PushNotifications.register();

        // Cuando Firebase nos da el token, enviarlo al backend
        PushNotifications.addListener('registration', async (token) => {
            console.log('✅ FCM Token:', token.value);
            try {
                await fetch(`${API_URL}/users/fcm-token`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId, fcmToken: token.value })
                });
                console.log('✅ FCM Token registrado en backend');
            } catch (err) {
                console.error('❌ Error guardando FCM token:', err);
            }
        });

        // Error al registrar
        PushNotifications.addListener('registrationError', (error) => {
            console.error('❌ Error registrando FCM:', error);
        });

        // Notificación recibida con app abierta (foreground) — no mostrar badge
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('📩 Notificación recibida (foreground):', notification);
        });

        // Usuario toca la notificación — leer payload y rutear directamente
        // al chat (1-a-1 o grupo) o a la llamada entrante. Antes solo
        // limpiábamos el badge — el usuario tenía que navegar a mano.
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            try {
                const data = action?.notification?.data || {};
                const type = data.type || '';
                const conversationId = data.conversationId || '';
                const groupId = data.groupId || '';
                const callId = data.callId || '';

                // Llamada entrante: dejar que CallManager lo gestione (ya
                // hay un evento socket `incoming_call` paralelo cuando la
                // app abre/reanuda; aquí solo aseguramos que la app llega
                // al panel correcto). Si callId está, navegamos a chats.
                if (type === 'incoming_call' || type === 'call') {
                    window.dispatchEvent(new CustomEvent('kaixo:open_call', { detail: { callId, conversationId, groupId } }));
                } else if (groupId) {
                    window.dispatchEvent(new CustomEvent('kaixo:open_chat', { detail: { groupId } }));
                } else if (conversationId) {
                    window.dispatchEvent(new CustomEvent('kaixo:open_chat', { detail: { conversationId } }));
                }
            } catch (err) {
                console.warn('[fcm] tap deep-link error:', err?.message);
            }
            try { PushNotifications.removeAllDeliveredNotifications(); } catch {}
        });

        // Limpiar badge al abrir la app
        PushNotifications.removeAllDeliveredNotifications();

    } catch (err) {
        console.warn('⚠️ FCM no disponible:', err.message);
    }
}

/**
 * Limpiar todas las notificaciones y badge del icono
 */
export async function clearFCMNotifications() {
    if (!Capacitor.isNativePlatform()) return;
    try {
        await PushNotifications.removeAllDeliveredNotifications();
    } catch {}
}
